import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { combineLatest, Observable, of } from 'rxjs';
import { tap } from 'rxjs/operators';
import {
  selectOnboardingBaseUrl,
  selectOnboardingClientId,
  selectOnboardingClientSecret,
} from '../store/selectors/onboarding-config.selectors';

export type ClientCredentials = {
  access_token: string;
  scope: string;
  expires_in: number;
  token_type: string;
};

export type GrantCodeCredentials = {
  access_token: string;
  scope: string;
  expires_in: number;
  token_type: string;
  id_token: string;
};

type AuthorizeResponse = AuthorizeErrorResponse | AuthorizeSuccessResponse;
type AuthorizeErrorResponse = {
  state: string;
  type: 'error';
  error: string;
  error_description: string;
};
type AuthorizeSuccessResponse = {
  state: string;
  type: 'code';
  code: string;
};

@Injectable({
  providedIn: 'root',
})
export class OnboardingAuthService {
  onboardingBaseUrl: string;
  clientCredentials?: ClientCredentials;
  basicHTTPAuthHeader: string;

  constructor(
    private http: HttpClient,
    private store: Store,
  ) {
    this.store.pipe(selectOnboardingBaseUrl).subscribe((url) => {
      this.onboardingBaseUrl = url;
    });
    combineLatest([
      this.store.pipe(selectOnboardingClientId),
      this.store.pipe(selectOnboardingClientSecret),
    ]).subscribe(([onboardingClientId, onboardingClientSecret]) => {
      if (onboardingClientId && onboardingClientSecret) {
        const encodedBasicAuth = btoa(
          `${onboardingClientId}:${onboardingClientSecret}`,
        );
        this.basicHTTPAuthHeader = `Basic ${encodedBasicAuth}`;
      } else {
        console.error(
          'missing app configs onboardingClientId and/or onboardingClientSecret',
        );
        this.basicHTTPAuthHeader = '';
      }
    });
  }

  getClientCredentials(updateScope?: boolean): Observable<ClientCredentials> {
    if (this.clientCredentials) {
      return of(this.clientCredentials);
    }

    const body = this.serializeParamsObject({
      grant_type: 'client_credentials',
      scope: updateScope ? 'account/Password.reset' : 'account/create',
    });

    const headers = {
      'content-type': 'application/x-www-form-urlencoded',
      authorization: this.basicHTTPAuthHeader,
    };

    return this.http
      .post<ClientCredentials>(`${this.onboardingBaseUrl}/oidc/token`, body, {
        headers,
      })
      .pipe(
        tap(
          (clientCredentials) => (this.clientCredentials = clientCredentials),
        ),
      );
  }

  private serializeParamsObject(bodyObject) {
    return Object.entries(bodyObject)
      .map(([key, value]) => `${key}=${value}`)
      .join('&');
  }
}
